import { localize } from "@/i18n/translate";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { api, errorCode } from "@/lib/api";
import { useAuth } from "../AuthContext";
import { usePortal } from "../i18n";
import { Badge, ErrorNote, PageTitle, Spinner, btnPrimary, btnSecondary } from "../ui";
export const TicketRow = ({
  t,
  to,
  showClient
}) => {
  const {
    p,
    fmtDate
  } = usePortal();
  const unread = showClient ? t.unread_admin : t.unread_client;
  return <li>
      <Link to={to} data-testid={`ticket-row-${t.ref}`} className="group grid gap-3 border-b border-ink/15 py-5 transition-colors hover:bg-white/50 md:grid-cols-[1fr_auto] md:items-center md:px-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-xs text-mute">{localize(p.dash.ref)} {localize(t.ref)}</span>
            <Badge value={t.status} label={p.ticket[t.status]} />
            {localize(unread && <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-signal"><span className="h-1.5 w-1.5 bg-signal" />{localize(showClient ? p.dash.newMessage : p.dash.newReply)}</span>)}
          </div>
          <p className="mt-2 truncate font-display text-lg font-bold tracking-[-0.01em] group-hover:text-signal">{localize(t.subject)}</p>
          <p className="mt-1 text-sm text-mute">
            {localize(showClient && <>{localize(t.user_name)}{localize(t.user_organisation ? ` · ${t.user_organisation}` : "")} · </>)}
            {localize(p.categories[t.category])} · {localize(p.dash.updated)} {localize(fmtDate(t.updated_at))}
          </p>
        </div>
        <ArrowRight className="hidden h-5 w-5 text-mute transition-transform group-hover:translate-x-1 group-hover:text-signal md:block" aria-hidden="true" />
      </Link>
    </li>;
};
const MembershipCard = () => {
  const {
    user
  } = useAuth();
  const {
    p,
    membershipLabel
  } = usePortal();
  return <section className="border border-ink/15 bg-white/60 p-6" data-testid="membership-card">
      <p className="eyebrow">{localize(p.dash.membership)}</p>
      <p className="mt-3 font-display text-xl font-bold" data-testid="membership-value">{localize(user?.membership ? membershipLabel(user.membership) : p.dash.noMembership)}</p>
      <p className="mt-3 text-sm leading-relaxed text-mute">{localize(p.dash.membershipHint)}</p>
      {localize(!user?.membership && <Link to="/kundenbereich/anfragen/neu?thema=Mitgliedschaft" className={`${btnSecondary} mt-5`} data-testid="membership-request-btn">{localize(p.dash.membershipAsk)}</Link>)}
    </section>;
};
export default function ClientDashboard() {
  const {
    user
  } = useAuth();
  const {
    p,
    errText
  } = usePortal();
  const [tickets, setTickets] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get("/portal/tickets").then(r => setTickets(Array.isArray(r.data) ? r.data : [])).catch(e => {
      setErr(errText(errorCode(e)));
      setTickets([]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <div data-testid="client-dashboard">
      <PageTitle eyebrow={p.area} title={localize(`${p.dash.hello}, ${user?.name || ""}`)}>
        <Link to="/kundenbereich/anfragen/neu" className={btnPrimary} data-testid="new-ticket-btn"><Plus className="h-4 w-4" aria-hidden="true" /> {localize(p.dash.newTicket)}</Link>
      </PageTitle>
      <p className="mt-6 max-w-xl text-mute">{localize(p.dash.intro)}</p>
      <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_300px]">
        <section aria-labelledby="my-tickets">
          <h2 id="my-tickets" className="font-display text-2xl font-bold tracking-[-0.02em]">{localize(p.dash.myTickets)}</h2>
          <ErrorNote>{localize(err)}</ErrorNote>
          {localize(tickets === null ? <Spinner label={p.loading} /> : tickets.length === 0 ? <div className="mt-6 border border-dashed border-ink/25 p-10 text-center" data-testid="tickets-empty">
              <p className="text-mute">{localize(p.dash.empty)}</p>
              <Link to="/kundenbereich/anfragen/neu" className={`${btnSecondary} mt-6`}>{localize(p.dash.emptyCta)}</Link>
            </div> : <ul className="mt-4 border-t border-ink/15" data-testid="tickets-list">
              {localize(tickets.map(t => <TicketRow key={t.id} t={t} to={`/kundenbereich/anfragen/${t.id}`} />))}
            </ul>)}
        </section>
        <MembershipCard />
      </div>
    </div>;
}
